// Third party
import {
  DataGridPro, GridApiPro, GridCellParams, GridColDef,
} from '@mui/x-data-grid-pro';
import Typography from '@mui/material/Typography';
import startCase from 'lodash/startCase';
import { useTranslation } from 'react-i18next';
import get from 'lodash/get';

// Components
import { useTheme } from '@mui/material';
import {
  FC, MutableRefObject, useCallback, useMemo, useRef, useState,
} from 'react';
import Box from '@mui/material/Box';
import { useNavigate } from 'react-router-dom';
import LinearProgress from '@mui/material/LinearProgress';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { useScreenResize } from '@mgm/common/hooks/useResize';
import { BadgeStatus, BaseTooltip } from '@mgm/common/components/base';
import { dateFormatTZ, formatDecimalHours, toFixedString } from '@mgm/common/utils';
import { TimeoffTooltip } from '@mgm/common/features/timesheets';
import { useIsFetching } from '@tanstack/react-query';
import { CustomNoRowsOverlay } from '@mgm/common/components';
import { TimesheetsTableHeader } from './TimesheetsTableHeader';
import { MemoizedFilterTabs } from '../../../../components/base';
import { TableActions } from './TableActions';

// Hooks
import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppStore';
import { useSheets } from '../../../../hooks/useSheets';
import { useSheetsPermissions } from '../../hooks';

// Redux-toolkit
import { getCurrencyById, getIsPrintMode } from '../../../../store/common/selectors';
import { getAuthenticateUser } from '../../../../store/authenticate/selectors';
import { useTable } from '../../../../hooks/useTable';
import { getIsOpenEmployeesExportMode, getSheets } from '../../../../store/sheets/selectors';
import { sheetsActions } from '../../../../store/sheets/slice';
import { PrintWrapper } from '../../../../layout/wrapper/PrintWrapper';
import { useGetSheetsAPI } from '../../../../hooks/api/useSheets';
import { getPersonnelStatus } from '../../../../store/clock/selectors';
import { useCommon } from '../../../../hooks/useCommon';
import { INDEX_TO_COLOR } from '../../../../data';
import { QUERY_KEYS } from '../../../../utils/enums';

type TTimesheetsTable = {
  apiRef: MutableRefObject<GridApiPro>
};

const TimesheetsTable: FC<TTimesheetsTable> = ({ apiRef }) => {
  const { t } = useTranslation(['timesheets', 'inputs', 'common', 'clock', 'schedule']);
  const getCurrency = useAppSelector(getCurrencyById);
  const navigate = useNavigate();
  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);
  const { localText, handleExport } = useTable(apiRef);
  const { pageHeight, resizableDiv, isDesktopSmallScreen } = useScreenResize(isMobile ? 20 : 40);
  const { permissions } = useSheetsPermissions();
  const { personnel } = useAppSelector(getAuthenticateUser);
  const dispatch = useAppDispatch();
  const componentRef = useRef<HTMLDivElement | null>(null);
  const isPrintMode = useAppSelector(getIsPrintMode);
  const sheets = useAppSelector(getSheets);
  const personnelStatus = useAppSelector(getPersonnelStatus);
  const { locationsByPermissions } = useCommon();
  const isOpenEmployeesExportMode = useAppSelector(getIsOpenEmployeesExportMode);

  const isFetchingSheets = useIsFetching({
    queryKey: [`${QUERY_KEYS.TIMESHEETS}-${personnel?.email}`],
  });

  const [isFullTable, setIsFullTable] = useState(false);

  const { isLoading } = useGetSheetsAPI({ isMainSheetsPage: true });

  const { getSheetsByFilters, getWorkTimes } = useSheets();

  const getLengthByStatus = (locationId: number) => sheets.reduce(
    (count, item) => (item.personnel.locations.some(({ id }) => id === locationId) ? count + 1 : count),
    0,
  );

  const handleFilterStatus = (newValue: number) => {
    dispatch(sheetsActions.setSelectedLocationId(newValue));
  };

  const tabs = useMemo(() => {
    const baseTabs = locationsByPermissions.map((location, index) => ({
      value: location.id,
      label: location.name,
      color: INDEX_TO_COLOR[index],
      count: getLengthByStatus(location.id),
    }));

    baseTabs.unshift({
      value: 0,
      label: t('all', { ns: 'common' }),
      color: 'info',
      count: sheets.length,
    });

    return baseTabs;
  }, [locationsByPermissions, sheets]);

  const onCellDoubleClick = (params: GridCellParams) => {
    dispatch(sheetsActions.setSelectedMember(params.row));
    navigate(`:${params.row.personnel?.name}`);
  };

  const renderCustomNoRowsOverlay = () => <CustomNoRowsOverlay text={t('itLooksLikeThereAreNoData', { ns: 'common' })} />;

  const timesheetsColumns: Array<GridColDef> = [
    {
      field: 'name',
      headerName: t('name', { ns: 'inputs' }),
      minWidth: 200,
      renderCell: ({ row }) => {
        const { formattedBreak, formattedTime } = getWorkTimes(personnelStatus[row.personnel.email]);
        const inActive = personnelStatus[row.personnel.email]?.status === 'inActive';

        const inBreak = personnelStatus[row.personnel.email]?.status === 'inBreak';
        return (
          <Box id="avatar-status" display="flex" alignItems="center" height="100%">
            {!inActive ? (
              <BaseTooltip tooltipText={(
                <Stack>
                  <Typography variant="caption">
                    {t('clockedInAt', { ns: 'clock' })}
                    {' '}
                    {dateFormatTZ(personnelStatus[row.personnel.email]?.shiftStart || '', 'hh:mm A')}
                    {' '}
                    {t('via', { ns: 'clock' })}
                    {' '}
                    {personnelStatus[row.personnel.email]?.clockInVia}
                  </Typography>
                  <Typography variant="caption">
                    {inBreak ? formattedBreak : formattedTime}
                  </Typography>
                </Stack>
              )}
              >
                <BadgeStatus
                  status={personnelStatus[row.personnel.email]?.status}
                  name={row.personnel?.name}
                  avatar={row.personnel.avatarFile}
                />
              </BaseTooltip>
            ) : (
              <BadgeStatus
                status={personnelStatus[row.personnel.email]?.status}
                name={row.personnel?.name}
                avatar={row.personnel.avatarFile}
              />
            )}
            <Stack>
              <Typography pl={1} variant="caption">
                {startCase(row.personnel.name)}
              </Typography>
            </Stack>
          </Box>
        );
      },
      valueGetter: (_, row) => row.personnel.name,
    },
    {
      field: 'payment.paymentAmount',
      headerName: t('wage', { ns: 'inputs' }),
      minWidth: 170,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography component="div" variant="caption">
            {permissions.isAllowViewSalaryData
            || row.personnel.email === personnel.email ? (
              <div>
                {get(row, 'payment.paymentAmount', '/')}
              </div>
              ) : '/'}
          </Typography>
        </Box>
      ),
      valueGetter: (_, row) => get(row, 'payment.paymentAmount', '/'),
    },
    {
      field: 'shifts',
      headerName: t('shifts', { ns: 'common' }),
      minWidth: 170,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography component="div" variant="caption">
            {row.shifts.length || 0}
          </Typography>
        </Box>
      ),
      valueGetter: (_, row) => row.shifts.length,
    },
    {
      headerName: t('workTimes'),
      field: 'totals.totalHours',
      minWidth: 170,
      renderCell: ({ row }) => {
        const totalHours = get(row, 'totals.sheets.totalHours', '/');
        const totalHoursTooltip = get(row, 'totals.sheets.totalHoursTooltip', '/');
        return (
          <BaseTooltip tooltipText={totalHoursTooltip}>
            <Typography variant="caption">
              {totalHours !== '/' ? formatDecimalHours(totalHours) : '/'}
            </Typography>
          </BaseTooltip>
        );
      },
      valueGetter: (_, row) => {
        const totalHours = get(row, 'totals.sheets.totalHours', '/');

        return totalHours !== '/' ? formatDecimalHours(totalHours) : '/';
      },
    },
    {
      headerName: t('breakTimes'),
      field: 'totals.totalBreakTime',
      minWidth: 170,
      renderCell: ({ row }) => {
        const breakTime = get(row, 'totals.sheets.totalBreakTime', 0);
        const totalBreakTime = breakTime > 0 ? breakTime / 3600 : 0;
        return (
          <Typography variant="caption">
            {toFixedString(totalBreakTime, 2)}
          </Typography>
        );
      },
      valueGetter: (_, row) => get(row, 'totals.sheets.totalBreakTime', '/'),
    },
    {
      headerName: t('amount'),
      field: 'totals.totalPay',
      minWidth: 170,
      renderCell: ({ row }) => {
        const userCurrency = getCurrency(row.personnel.paymentCurrencyId);

        const isValidTotalPay = get(row, 'totals.totalPay', '/') !== '/' && !row.totals.totalPay.startsWith('NaN');

        const val = isValidTotalPay && (personnel.email === row.personnel.email || permissions.isAllowViewSalaryData)
          ? `${userCurrency.mark}${row.totals.totalPay}` : '/';

        return (
          <Typography variant="caption">
            {val}
          </Typography>
        );
      },
      valueGetter: (_, row) => get(row, 'totals.totalPay', '/'),
    },
    {
      headerName: 'Total PTO',
      field: 'totals.totalPay.timeoff.totalPTO',
      minWidth: 170,
      renderCell: ({ row }) => {
        const value = get(row, 'totals.timeoff.totalPTO', 0);
        const vacationsValue = get(row, 'totals.timeoff.vacationDaysCount', 0);
        const otherValue = get(row, 'totals.timeoff.otherDaysCount', 0);

        return (
          <BaseTooltip tooltipText={(
            <TimeoffTooltip
              vacations={vacationsValue}
              other={otherValue}
              locale={{
                vacations: t('vacations', { ns: 'schedule' }),
                other: t('other', { ns: 'schedule' }),
              }}
            />
          )}
          >
            <Typography variant="caption">
              {value}
            </Typography>
          </BaseTooltip>
        );
      },
      valueGetter: (_, row) => get(row, 'totals.timeoff.totalPTO', '/'),
    },
    {
      headerName: t('totalSickLeave'),
      field: 'totals.timeoff.totalSickLeave',
      minWidth: 170,
      renderCell: ({ row }) => {
        const value = get(row, 'totals.timeoff.totalSickLeave', 0);

        return (
          <Typography variant="caption">
            {value}
          </Typography>
        );
      },
      valueGetter: (_, row) => get(row, 'totals.timeoff.totalSickLeave', '/'),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('actions', { ns: 'common' }),
      disableExport: true,
      sortable: false,
      align: 'right',
      getActions: ({ row }) => {
        const actions = [];
        if (row.personnel.email === personnel.email || permissions.isAllowViewSalaryData) {
          actions.push(<TableActions chosenMember={row} />);
        }

        return actions;
      },
    },
  ];

  const renderTableHeader = useCallback(() => (
    <TimesheetsTableHeader
      printRef={componentRef}
      apiRef={apiRef}
      isFullTable={isFullTable}
      setIsFullTable={setIsFullTable}
      handleExport={() => {
        apiRef.current.setPinnedColumns({ left: ['name'], right: ['actions'] });
        handleExport('mgm-timesheets');
      }}
    />
  ), [apiRef, isFullTable]);

  return (
    <PrintWrapper componentRef={componentRef}>
      <div ref={resizableDiv}>
        <Card
          sx={{
            padding: 0,
            width: 'auto',
            px: 0,
            pb: 6,
            // eslint-disable-next-line no-nested-ternary
            height: isDesktopSmallScreen ? 'auto' : isFullTable && getSheetsByFilters?.length > 0 ? 'auto' : pageHeight !== 0 && !isPrintMode ? pageHeight : 'auto',
            ...(isMobile && {
              height: 'auto',
              background: 'unset',
              boxShadow: 'unset',
              px: 0,
              pt: 0,
              pb: '35px',
            }),
          }}
        >
          <div id="timesheets-locations">
            <MemoizedFilterTabs tabs={tabs} onFilterChange={handleFilterStatus} />
          </div>
          <DataGridPro
            apiRef={apiRef}
            rows={getSheetsByFilters}
            columnBufferPx={100}
            rowBufferPx={100}
            loading={isLoading || isFetchingSheets as unknown as boolean}
            {...(!isMobile && {
              initialState: { pinnedColumns: { left: ['name'], right: ['actions'] } },
            })}
            slots={{
              loadingOverlay: LinearProgress as any,
              toolbar: renderTableHeader as any,
              noRowsOverlay: renderCustomNoRowsOverlay,
            }}
            sx={{
              '.MuiDataGrid-main > *:first-of-type': {
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
              },
              px: 0,
            }}
            showToolbar
            localeText={localText}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                printOptions: {
                  hideFooter: true,
                  hideToolbar: true,
                },
              },
            }}
            getRowId={(row) => row.personnel.email}
            columns={timesheetsColumns}
            hideFooterPagination
            hideFooterSelectedRowCount
            onRowSelectionModelChange={(emails) => {
              dispatch(sheetsActions.setSelectedMembers(emails));
            }}
            checkboxSelection={isOpenEmployeesExportMode}
            onCellDoubleClick={onCellDoubleClick}
            hideFooter
            className={isPrintMode ? 'print-mode' : ''}
          />
        </Card>
      </div>
    </PrintWrapper>
  );
};

export {
  TimesheetsTable,
};
